import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryGetMe } from "@/apis/users/getMe";
import { useQueryGetExamPaperStatus } from "@/apis/exam_progress/getExamPaperStatus";
import { useMutationSubmitModule } from "@/apis/exam_progress/submitModule";
import { useQueryGetHighlights, useSaveHighlights } from "@/apis/questions";
import { useQueryGetExamConfig } from "@/apis/exam_configs/getExamConfig";
import ExamHeader from "./components/ExamHeader";
import ExamFooter from "./components/ExamFooter";
import ExamPassage from "./components/ExamPassage";
import ExamQuestionPanel from "./components/ExamQuestionPanel";
import CalculatorModal from "./components/CalculatorModal";
import ReviewPanel from "./components/ReviewPanel";
import ExamCompletedPanel from "./components/ExamCompletedPanel";
import ExamWatermark from "./components/ExamWatermark";
import { useExamContext } from "@/pages/my_exams/context/ExamContext";
import { useTimer } from "@/hooks/useTimer";
import ExamTimeLimitToast from "./components/ExamTimeLimitToast";
import { EXAM_PAPER_TYPE_READING_WRITING } from "@/constants/exam";
import ExamNoActiveSession from "./components/ExamNoActiveSession";
import ExamLoadingView from "./components/ExamLoadingView";
import type { IAnswerState } from "@/pages/my_exams/context/ExamContext";
import ModuleEndPanel from "./components/ModuleEndPanel";
import { useQueryClient } from "@tanstack/react-query";

const ExamStartPage: React.FC = () => {
  const { id, exam_paper_id } = useParams<{
    id: string;
    exam_paper_id: string;
  }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const questionParam = searchParams.get("question");
  const { data: me, isLoading: isMeLoading } = useQueryGetMe();
  const { data: progressStatus, isLoading: isStatusLoading } =
    useQueryGetExamPaperStatus(id || "", exam_paper_id || "");
  
  // Get exam config for current exam type
  const { data: examConfig } = useQueryGetExamConfig(
    progressStatus?.paper?.exam_type || "",
    {
      enabled: !!progressStatus?.paper?.exam_type,
    }
  );

  // Get current subject's passage type
  const currentSubjectPassageType = useMemo(() => {
    if (!examConfig?.subjects || !progressStatus?.paper?.subject) return undefined;
    
    const currentSubject = examConfig.subjects.find(
      (subject) => subject.subject === progressStatus.paper?.subject
    );
    return currentSubject?.passage_type;
  }, [examConfig, progressStatus?.paper?.subject]);

  const [timerHidden, setTimerHidden] = useState(false);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const { excludedChoices, setExcludedChoices, answers, setAnswers } =
    useExamContext();
  const [showExitModal, setShowExitModal] = useState(false);
  const [showEarlySubmitModal, setShowEarlySubmitModal] = useState(false);
  const {
    mutate: submitModule,
    isPending: isSubmitting,
    error: submitModuleError,
  } = useMutationSubmitModule();

  // 서버 기반 정확한 종료 시간 계산
  const calculatedEndTime = useMemo(() => {
    const paper = progressStatus?.paper;
    
    if (paper?.server_time && paper?.remaining_seconds) {
      const clientTime = Date.now();
      
      // 클라이언트 기준으로 언제 끝날지 계산
      const endTime = clientTime + (paper.remaining_seconds * 1000);
      
      return new Date(endTime).toISOString();
    }
    
    // 서버 데이터가 없으면 기존 ends_at 사용 (fallback)
    return progressStatus?.paper?.ends_at;
  }, [progressStatus?.paper]);

  // Hook을 최상위 레벨에서 한 번만 호출
  const { remainingSeconds, formatted } = useTimer(calculatedEndTime);

  // 5분 남았을 때 알림 상태
  const [showFiveMinAlert, setShowFiveMinAlert] = useState(false);
  const [fiveMinAlertShown, setFiveMinAlertShown] = useState(false);

  const prevRemainingRef = React.useRef<number | null>(null);

  useEffect(() => {
    if (
      progressStatus?.status === "in_progress" &&
      typeof remainingSeconds === "number"
    ) {
      if (
        prevRemainingRef.current !== null &&
        prevRemainingRef.current > 300 &&
        remainingSeconds <= 300
      ) {
        setShowFiveMinAlert(true);
        setFiveMinAlertShown(true);
      }
      prevRemainingRef.current = remainingSeconds;
    }
  }, [progressStatus?.status, remainingSeconds]);

  // 초기 로딩 시 서버에서 내려온 답변을 answers에 세팅
  useEffect(() => {
    if (
      progressStatus?.status === "in_progress" &&
      Object.keys(answers).length === 0
    ) {
      const newAnswers: Record<string, IAnswerState> = {};
      (progressStatus as any)?.answers?.forEach((ans: any) => {
        newAnswers[ans.question_id] = {
          selected_choice_id: ans.selected_answer,
          excluded_choices: ans.excluded_choices || [],
          marked_for_review: ans.marked_for_review || false,
        };
      });
      setAnswers(newAnswers);
    }
  }, [progressStatus, answers.length, setAnswers]);

  // 문제 인덱스: 쿼리 파라미터에서 파싱 (in_progress 상태에서만)
  let questions: any[] = [];
  let isReview = false;
  let currentQuestionIdx = 0;
  let question: any = null;
  let isFirstQuestion = true;
  let isLastQuestion = false;
  if (progressStatus && progressStatus.status === "in_progress") {
    questions = (progressStatus as any).questions || [];
    isReview = questionParam === "review";
    currentQuestionIdx =
      !isReview && questionParam ? Number(questionParam) - 1 : 0;
    question = questions[currentQuestionIdx];
    isFirstQuestion = currentQuestionIdx === 0;
    isLastQuestion = currentQuestionIdx === questions.length - 1;
  }

  // Highlight data management
  const { data: highlightsData } = useQueryGetHighlights(
    id || "",
    question?.passage_id ? undefined : question?.id || "",
    question?.passage_id || undefined,
    {
      enabled: !!id && (!!question?.id || !!question?.passage_id),
    }
  );
  const { mutate: saveHighlights } = useSaveHighlights();

  // Get highlighted content or fallback to original
  const highlightedPassage =
    highlightsData?.highlighted_passage || question?.passage || "";
  const highlightedQuestion =
    highlightsData?.highlighted_question_text || question?.question || "";

  // Highlight save callback
  const handleSaveHighlight = useCallback(
    (contentType: "passage" | "question", newContent: string) => {
      if (!id || (!question?.id && !question?.passage_id)) return;

      const saveData = {
        exam_progress_id: id,
        ...(question?.passage_id
          ? { passage_id: question.passage_id }
          : { question_id: question.id }),
        ...(contentType === "passage"
          ? { highlighted_passage: newContent }
          : { highlighted_question_text: newContent }),
      };
      saveHighlights(saveData);
    },
    [id, question?.id, question?.passage_id, saveHighlights]
  );

  // Mark for Review 상태인 문제들의 인덱스 배열
  const forReviewQuestions = questions
    .map((q, idx) => (answers[q.id]?.marked_for_review ? idx : -1))
    .filter((idx) => idx !== -1);

  // 정답을 실제로 채택한 문제들의 인덱스 배열 계산 함수
  const getAnsweredQuestions = () => {
    return Object.keys(answers)
      .filter((qId) => {
        const answer = answers[qId];
        const question = questions.find((q: any) => q.id === qId);

        // selected_choice_id가 존재하고 유효한 값인지 확인
        if (!answer?.selected_choice_id) return false;

        // 단답형의 경우 빈 값이 아닌지 추가 확인
        if (question?.answer_type === "short_answer") {
          return answer.selected_choice_id.trim().length > 0;
        }

        // 객관식의 경우 선택된 choice_id가 있으면 OK
        return true;
      })
      .map((qId) => questions.findIndex((q: any) => q.id === qId))
      .filter((idx: number) => idx !== -1);
  };

  // 문제 직접 선택 시
  const handleQuestionSelect = (idx: number) => {
    setSearchParams({ question: String(idx + 1) });
  };

  // progressStatus.data.answers에서 excludedChoices, answers 초기화
  useEffect(() => {
    if (!progressStatus || progressStatus.status !== "in_progress") return;
    const answers = (progressStatus as any).answers || [];
    // 이미 세팅된 경우 중복 초기화 방지
    if (
      Object.keys(excludedChoices).length > 0 ||
      Object.keys(answers).length > 0
    )
      return;
    const newExcluded: { [questionId: string]: string[] } = {};
    const newAnswers: Record<string, IAnswerState> = {};
    answers.forEach((ans: any) => {
      if (ans.selected_answer)
        newAnswers[ans.question_id] = {
          selected_choice_id: ans.selected_answer,
          excluded_choices: ans.excluded_choices || [],
          marked_for_review: false,
        };
      if (ans.excluded_choices)
        newExcluded[ans.question_id] = ans.excluded_choices;
    });
    setExcludedChoices(newExcluded);
    setAnswers(newAnswers);
  }, [progressStatus]);

  const [showModuleEndPanel, setShowModuleEndPanel] = useState(false);
  const [moduleEndReady, setModuleEndReady] = useState(false);
  const [isTimerExpired, setIsTimerExpired] = useState(false);
  const isNextingRef = useRef(false);
  const queryClient = useQueryClient();

  // 조기 제출 핸들러
  const handleEarlySubmit = () => {
    setShowEarlySubmitModal(false);
    setMoreOpen(false);
    setShowModuleEndPanel(true);
    setModuleEndReady(false);

    const timerPromise = new Promise((resolve) => setTimeout(resolve, 2000));
    const submitModulePromise = new Promise<void>((resolve) => {
      if (id && exam_paper_id) {
        submitModule(
          { progressId: id, examPaperId: exam_paper_id },
          {
            onSuccess: async () => {
              await queryClient.invalidateQueries({
                queryKey: ["examPaperStatus", id, exam_paper_id],
              });
              resolve();
            },
            onError: () => resolve(),
          }
        );
      } else {
        resolve();
      }
    });

    Promise.all([timerPromise, submitModulePromise]).then(() => {
      setModuleEndReady(true);
    });
  };

  // 타이머 만료 감지
  useEffect(() => {
    if (
      remainingSeconds === 0 &&
      !isTimerExpired &&
      progressStatus?.status === "in_progress" &&
      !isNextingRef.current
    ) {
      setIsTimerExpired(true);
      isNextingRef.current = true;

      setShowModuleEndPanel(true);
      setModuleEndReady(false);
      const timerPromise = new Promise((resolve) => setTimeout(resolve, 2000));
      const submitModulePromise = new Promise<void>((resolve) => {
        if (id && exam_paper_id) {
          submitModule(
            { progressId: id, examPaperId: exam_paper_id },
            {
              onSuccess: async () => {
                await queryClient.invalidateQueries({
                  queryKey: ["examPaperStatus", id, exam_paper_id],
                });
                setIsTimerExpired(false);
                resolve();
              },
              onError: () => resolve(),
            }
          );
        } else {
          resolve();
        }
      });
      Promise.all([timerPromise, submitModulePromise]).then(() => {
        setModuleEndReady(true);
        isNextingRef.current = false;
      });
    }
  }, [remainingSeconds, isTimerExpired, progressStatus?.status]);

  // 모듈 종료 패널 처리
  useEffect(() => {
    if (
      showModuleEndPanel &&
      moduleEndReady &&
      (progressStatus?.status === "break_time" ||
        progressStatus?.status === "in_progress" ||
        progressStatus?.status === "completed")
    ) {
      setShowModuleEndPanel(false);
      if (progressStatus?.status === "in_progress") {
        setSearchParams({ question: "1" });
      }
      // completed일 때는 쿼리 파라미터 변경 없이 패널만 닫음
    }
  }, [progressStatus, showModuleEndPanel, moduleEndReady]);

  // 로딩 상태 처리
  if (isMeLoading || isStatusLoading) {
    return <ExamLoadingView />;
  }

  // 상태별 분기 렌더링
  if (!progressStatus) {
    return <ExamNoActiveSession />;
  }

  if (showModuleEndPanel) {
    return <ModuleEndPanel examType={progressStatus?.paper?.exam_type} />;
  }

  if (progressStatus.status === "completed") {
    return <ExamCompletedPanel examType={progressStatus?.paper?.exam_type} />;
  }

  // 리뷰 단계 분기
  if (isReview) {
    // 상태 계산
    const answeredQuestions = getAnsweredQuestions();
    // For Review: marked_for_review === true
    const forReviewQuestions =
      progressStatus && progressStatus.status === "in_progress"
        ? Object.keys(answers)
            .map((qId) =>
              answers[qId]?.marked_for_review
                ? questions.findIndex((q: any) => q.id === qId)
                : -1
            )
            .filter((idx: number) => idx !== -1)
        : [];

    // ExamFooter에 Next 버튼 핸들러와 로딩/에러 상태 전달
    return (
      <div className="flex flex-col h-screen bg-[#f7f8fa]">
        <ExamHeader
          timer={formatted}
          timerHidden={timerHidden}
          onToggleTimerHidden={() => setTimerHidden((v) => !v)}
          calculatorOpen={calculatorOpen}
          onToggleCalculator={() => setCalculatorOpen((v) => !v)}
          moreOpen={moreOpen}
          onToggleMore={() => setMoreOpen((v) => !v)}
          moreMenu={
            moreOpen && (
              <div
                className="absolute z-50 right-0 mt-2 w-48 bg-white rounded shadow-xl border border-gray-300"
                style={{ top: 60 }}
              >
                {me?.allow_early_submission && (
                  <button
                    className="w-full text-left px-4 py-2 text-sm hover:bg-orange-100 text-orange-600 font-bold border-b border-gray-200 cursor-pointer"
                    onClick={() => {
                      setShowEarlySubmitModal(true);
                      setMoreOpen(false);
                    }}
                  >
                    Submit Early
                  </button>
                )}
                <button
                  className="w-full text-left px-4 py-2 text-sm hover:bg-red-100 text-red-600 font-bold cursor-pointer"
                  onClick={() => {
                    setShowExitModal(true);
                    setMoreOpen(false);
                  }}
                >
                  Exit the exam
                </button>
              </div>
            )
          }
          timerColor={
            progressStatus?.status === "in_progress" && remainingSeconds <= 300
              ? "text-red-600"
              : "text-[#22336b]"
          }
        />
        <main className="flex-1 flex flex-col items-center justify-center">
          <ReviewPanel
            questions={questions}
            answeredQuestions={answeredQuestions}
            forReviewQuestions={forReviewQuestions}
            onQuestionSelect={handleQuestionSelect}
          />
        </main>
        <ExamFooter
          studentName={me?.name || "Student"}
          currentQuestionIdx={currentQuestionIdx}
          totalQuestions={questions.length}
          disablePrev={false}
          disableNext={false}
          sectionTitle={
            progressStatus?.paper?.exam_type === "SAT"
              ? `Module ${progressStatus?.paper?.current_module || 1}`
              : ""
          }
          onQuestionSelect={handleQuestionSelect}
          answeredQuestions={answeredQuestions}
          forReviewQuestions={forReviewQuestions}
          onReviewClick={() => setSearchParams({ question: "review" })}
          onReviewNext={async () => {
            setShowModuleEndPanel(true);
            setModuleEndReady(false);
            const timerPromise = new Promise((resolve) =>
              setTimeout(resolve, 2000)
            );
            const submitModulePromise = new Promise<void>((resolve) => {
              if (id && exam_paper_id) {
                submitModule(
                  { progressId: id, examPaperId: exam_paper_id },
                  {
                    onSuccess: async () => {
                      await queryClient.invalidateQueries({
                        queryKey: ["examPaperStatus", id, exam_paper_id],
                      });
                      resolve();
                    },
                    onError: () => resolve(),
                  }
                );
              } else {
                resolve();
              }
            });
            await Promise.all([timerPromise, submitModulePromise]);
            setModuleEndReady(true);
            // 패널은 useEffect에서 닫힘
          }}
        />
        <CalculatorModal
          isOpen={calculatorOpen}
          onClose={() => setCalculatorOpen(false)}
        />
        {showEarlySubmitModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/10">
            <div className="bg-white rounded-lg shadow-xl p-8 max-w-lg w-full relative animate-fade-in">
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl font-bold cursor-pointer"
                onClick={() => setShowEarlySubmitModal(false)}
                aria-label="Close"
              >
                ×
              </button>
              <div className="text-2xl font-bold mb-4">
                Submit Module Early?
              </div>
              <div className="mb-6 text-gray-700">
                Are you sure you want to submit this module early? You will not be able to return to this module once submitted.
              </div>
              <div className="flex justify-end gap-4">
                <button
                  className="text-blue-700 font-bold hover:underline px-4 py-2 cursor-pointer"
                  onClick={() => setShowEarlySubmitModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-6 py-2 shadow cursor-pointer"
                  onClick={handleEarlySubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit Early"}
                </button>
              </div>
            </div>
          </div>
        )}
        {showExitModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/10">
            <div className="bg-white rounded-lg shadow-xl p-8 max-w-lg w-full relative animate-fade-in">
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl font-bold cursor-pointer"
                onClick={() => setShowExitModal(false)}
                aria-label="Close"
              >
                ×
              </button>
              <div className="text-2xl font-bold mb-4">
                Are You Sure You Want to Exit the Preview?
              </div>
              <div className="mb-6 text-gray-700">
                Test previews are not timed or scored, and your progress isn't
                saved. If you exit and come back, you'll start over.
              </div>
              <div className="flex justify-end gap-4">
                <button
                  className="text-blue-700 font-bold hover:underline px-4 py-2 cursor-pointer"
                  onClick={() => setShowExitModal(false)}
                >
                  Continue Test Preview
                </button>
                <button
                  className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold rounded-full px-6 py-2 shadow border border-yellow-400 cursor-pointer"
                  onClick={() => navigate("/my_exams")}
                >
                  Exit
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // 문제 풀이 UI (in_progress 상태)
  return (
    <>
      <div className="flex flex-col h-screen bg-[#f7f8fa]">
        {/* 5분 남음 알림 Toast */}
        <ExamTimeLimitToast
          open={showFiveMinAlert}
          onClose={() => setShowFiveMinAlert(false)}
        />
        {/* Header */}
        <ExamHeader
          timer={formatted}
          timerHidden={timerHidden}
          onToggleTimerHidden={() => setTimerHidden((v) => !v)}
          calculatorOpen={calculatorOpen}
          onToggleCalculator={() => setCalculatorOpen((v) => !v)}
          moreOpen={moreOpen}
          onToggleMore={() => setMoreOpen((v) => !v)}
          moreMenu={
            moreOpen && (
              <div
                className="absolute z-50 right-0 mt-2 w-48 bg-white rounded shadow-xl border border-gray-300"
                style={{ top: 60 }}
              >
                {me?.allow_early_submission && (
                  <button
                    className="w-full text-left px-4 py-2 text-sm hover:bg-orange-100 text-orange-600 font-bold border-b border-gray-200 cursor-pointer"
                    onClick={() => {
                      setShowEarlySubmitModal(true);
                      setMoreOpen(false);
                    }}
                  >
                    Submit Early
                  </button>
                )}
                <button
                  className="w-full text-left px-4 py-2 text-sm hover:bg-red-100 text-red-600 font-bold cursor-pointer"
                  onClick={() => {
                    setShowExitModal(true);
                    setMoreOpen(false);
                  }}
                >
                  Exit the exam
                </button>
              </div>
            )
          }
          timerColor={
            progressStatus?.status === "in_progress" && remainingSeconds <= 300
              ? "text-red-600"
              : "text-[#22336b]"
          }
        />
        {/* Main Content */}
        <main className="flex-1 flex h-0 min-h-0 overflow-hidden bg-white justify-center">
          <ExamPassage
            passage={highlightedPassage}
            subject={progressStatus?.paper?.subject}
            examType={progressStatus?.paper?.exam_type}
            answer_type={question?.answer_type}
            examProgressId={id}
            questionId={question?.id}
            onSaveHighlight={handleSaveHighlight}
            passageType={currentSubjectPassageType}
          />
          <ExamQuestionPanel
            question={{
              ...question,
              question: highlightedQuestion,
            }}
            originalQuestion={question?.question || ""}
            questions={questions}
            onSaveHighlight={handleSaveHighlight}
          />
        </main>
        {/* Footer */}
        <ExamFooter
          studentName={me?.name || "Student"}
          currentQuestionIdx={currentQuestionIdx}
          totalQuestions={questions.length}
          disablePrev={isFirstQuestion}
          disableNext={false}
          sectionTitle={
            progressStatus?.paper?.exam_type === "SAT"
              ? `Module ${progressStatus?.paper?.current_module || 1}`
              : ""
          }
          onQuestionSelect={handleQuestionSelect}
          answeredQuestions={getAnsweredQuestions()}
          forReviewQuestions={forReviewQuestions}
          onReviewClick={() => setSearchParams({ question: "review" })}
        />
        {/* Calculator Modal */}
        <CalculatorModal
          isOpen={calculatorOpen}
          onClose={() => setCalculatorOpen(false)}
        />
        {/* Early Submit Modal */}
        {showEarlySubmitModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/10">
            <div className="bg-white rounded-lg shadow-xl p-8 max-w-lg w-full relative animate-fade-in">
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl font-bold cursor-pointer"
                onClick={() => setShowEarlySubmitModal(false)}
                aria-label="Close"
              >
                ×
              </button>
              <div className="text-2xl font-bold mb-4">
                Submit Module Early?
              </div>
              <div className="mb-6 text-gray-700">
                Are you sure you want to submit this module early? You will not be able to return to this module once submitted.
              </div>
              <div className="flex justify-end gap-4">
                <button
                  className="text-blue-700 font-bold hover:underline px-4 py-2 cursor-pointer"
                  onClick={() => setShowEarlySubmitModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-6 py-2 shadow cursor-pointer"
                  onClick={handleEarlySubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit Early"}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Exit Modal */}
        {showExitModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/10">
            <div className="bg-white rounded-lg shadow-xl p-8 max-w-lg w-full relative animate-fade-in">
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl font-bold cursor-pointer"
                onClick={() => setShowExitModal(false)}
                aria-label="Close"
              >
                ×
              </button>
              <div className="text-2xl font-bold mb-4">
                Are You Sure You Want to Exit the Preview?
              </div>
              <div className="mb-6 text-gray-700">
                Test previews are not timed or scored, and your progress isn't
                saved. If you exit and come back, you'll start over.
              </div>
              <div className="flex justify-end gap-4">
                <button
                  className="text-blue-700 font-bold hover:underline px-4 py-2 cursor-pointer"
                  onClick={() => setShowExitModal(false)}
                >
                  Continue Test Preview
                </button>
                <button
                  className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold rounded-full px-6 py-2 shadow border border-yellow-400 cursor-pointer"
                  onClick={() => navigate("/my_exams")}
                >
                  Exit
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <ExamWatermark />
    </>
  );
};

export default ExamStartPage;
